import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Ticket } from "@phosphor-icons/react";
import api, { errDetail } from "@/lib/api";
import { AdminShell, Field } from "@/components/AdminShell";

const EMPTY = { code: "", kind: "package", discount_pct: 100, valid_from: "", valid_until: "", max_uses_per_person: 3, max_total_uses: "", active: true, allowed_package_ids: [], allowed_pack_ids: [] };

const ScopePicker = ({ label, items, selected, onToggle, testPrefix, nameOf }) => (
  <div className="col-span-2 md:col-span-4" data-testid={`${testPrefix}-scope`}>
    <label className="block text-xs font-semibold text-gray-600 mb-1">{label} <span className="font-normal text-gray-400">(none checked = applies to all)</span></label>
    <div className="flex flex-wrap gap-2">
      {items.map((it) => (
        <label key={it.id} data-testid={`${testPrefix}-opt-${it.id}`}
          className={`flex items-center gap-1.5 text-xs border rounded-md px-2.5 py-1.5 cursor-pointer select-none ${selected.includes(it.id) ? "border-blue-400 bg-blue-50 text-blue-800 font-semibold" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`}>
          <input type="checkbox" data-testid={`${testPrefix}-cb-${it.id}`} className="accent-blue-600"
            checked={selected.includes(it.id)} onChange={() => onToggle(it.id)} />
          {nameOf(it)}
        </label>
      ))}
      {items.length === 0 && <span className="text-xs text-gray-400">None available</span>}
    </div>
  </div>
);

export default function AdminCodes() {
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [pkgs, setPkgs] = useState([]);
  const [packs, setPacks] = useState([]);
  const [reds, setReds] = useState({});

  const toggleReds = async (c) => {
    if (reds[c.id]) { setReds((r) => ({ ...r, [c.id]: null })); return; }
    try { const { data } = await api.get(`/admin/codes/${c.id}/redemptions`); setReds((r) => ({ ...r, [c.id]: data })); }
    catch (e) { toast.error(errDetail(e)); }
  };

  const load = () => api.get("/admin/codes").then((r) => setCodes(r.data)).catch((e) => toast.error(errDetail(e)));
  useEffect(() => {
    load();
    api.get("/admin/packages").then((r) => setPkgs(r.data)).catch(() => {});
    api.get("/admin/token-packages").then((r) => setPacks(r.data)).catch(() => {});
  }, []);

  const togglePkg = (id) => setForm((f) => ({ ...f, allowed_package_ids: f.allowed_package_ids.includes(id) ? f.allowed_package_ids.filter((x) => x !== id) : [...f.allowed_package_ids, id] }));
  const togglePack = (id) => setForm((f) => ({ ...f, allowed_pack_ids: f.allowed_pack_ids.includes(id) ? f.allowed_pack_ids.filter((x) => x !== id) : [...f.allowed_pack_ids, id] }));

  const appliesTo = (c) => {
    if (c.kind === "package") {
      if (!c.allowed_package_ids?.length) return "All plans";
      return c.allowed_package_ids.map((id) => pkgs.find((p) => p.id === id)?.name || "?").join(", ");
    }
    if (!c.allowed_pack_ids?.length) return "All packs";
    return c.allowed_pack_ids.map((id) => packs.find((p) => p.id === id)?.name || "?").join(", ");
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/admin/codes", { ...form, discount_pct: parseInt(form.discount_pct) || 100,
        max_uses_per_person: parseInt(form.max_uses_per_person) || 1,
        max_total_uses: form.max_total_uses === "" ? null : parseInt(form.max_total_uses),
        allowed_package_ids: form.kind === "package" ? form.allowed_package_ids : [],
        allowed_pack_ids: form.kind === "booster" ? form.allowed_pack_ids : [] });
      toast.success("Code created");
      setForm(EMPTY); load();
    } catch (e) { toast.error(errDetail(e)); }
    finally { setBusy(false); }
  };

  const toggle = async (c) => {
    try {
      await api.put(`/admin/codes/${c.id}`, { ...c, active: !c.active });
      toast.success(c.active ? "Code deactivated" : "Code activated"); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <AdminShell title="Access Codes" subtitle="Free or discounted access — 100% codes never reach Stripe. One use = one month for packages.">
      <div data-testid="admin-codes-page">
        <div className="border border-gray-200 rounded-md p-4 mb-6">
          <p className="text-xs font-bold text-gray-500 mb-3 flex items-center gap-1.5"><Ticket size={14} /> NEW CODE</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Code" testId="code-input" value={form.code} onChange={(v) => setForm({ ...form, code: v.toUpperCase() })} placeholder="FRIENDS2026" />
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Type</label>
              <select data-testid="code-kind-select" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-2.5 py-2 text-sm bg-white">
                <option value="package">Package (subscription)</option>
                <option value="booster">Booster (token pack)</option>
              </select>
            </div>
            <Field label="Discount % (100 = free)" testId="code-pct-input" type="number" value={form.discount_pct} onChange={(v) => setForm({ ...form, discount_pct: v })} />
            <Field label="Max uses per person" testId="code-peruse-input" type="number" value={form.max_uses_per_person} onChange={(v) => setForm({ ...form, max_uses_per_person: v })} />
            <Field label="Valid from" testId="code-from-input" type="date" value={form.valid_from} onChange={(v) => setForm({ ...form, valid_from: v })} />
            <Field label="Valid until" testId="code-until-input" type="date" value={form.valid_until} onChange={(v) => setForm({ ...form, valid_until: v })} />
            <Field label="Max total uses (empty = ∞)" testId="code-total-input" type="number" value={form.max_total_uses} onChange={(v) => setForm({ ...form, max_total_uses: v })} />
            <div className="flex items-end">
              <button data-testid="code-create-btn" onClick={save} disabled={busy || !form.code || !form.valid_from || !form.valid_until}
                className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-5 py-2 disabled:opacity-50 w-full">Create code</button>
            </div>
            {form.kind === "package" ? (
              <ScopePicker label="Applies to plans" items={pkgs} selected={form.allowed_package_ids} onToggle={togglePkg}
                testPrefix="code-pkg" nameOf={(p) => `${p.name} — $${p.price_monthly}/mo`} />
            ) : (
              <ScopePicker label="Applies to token packs" items={packs} selected={form.allowed_pack_ids} onToggle={togglePack}
                testPrefix="code-pack" nameOf={(p) => `${p.name} — ${p.tokens?.toLocaleString()} tokens · $${p.price_usd}`} />
            )}
          </div>
          <p className="text-[11px] text-gray-400 mt-2">Package codes: 100% activates instantly (no card, auto-renews monthly while valid, one use = one month). Partial % goes through Stripe with the discount pre-applied for the remaining months. Booster codes: 100% credits tokens instantly; partial % discounts the one-time payment.</p>
        </div>

        <div className="border border-gray-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr><th className="px-4 py-2.5">Code</th><th className="px-4 py-2.5">Type</th><th className="px-4 py-2.5">Discount</th>
                <th className="px-4 py-2.5">Applies to</th>
                <th className="px-4 py-2.5">Window</th><th className="px-4 py-2.5">Per person</th><th className="px-4 py-2.5">Used (months)</th>
                <th className="px-4 py-2.5">People</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5"></th></tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <React.Fragment key={c.id}>
                <tr data-testid={`code-row-${c.code}`} className="border-t border-gray-100">
                  <td className="px-4 py-2.5 font-mono font-semibold text-gray-800">
                    <button data-testid={`code-redemptions-btn-${c.code}`} onClick={() => toggleReds(c)}
                      className="hover:text-blue-600 hover:underline flex items-center gap-1">
                      {c.code} <span className="text-[10px] text-gray-400">{reds[c.id] ? "▲" : "▼"}</span>
                    </button>
                  </td>
                  <td className="px-4 py-2.5 text-gray-500 capitalize">{c.kind}</td>
                  <td className="px-4 py-2.5">{c.discount_pct === 100 ? <span className="text-emerald-700 font-semibold">FREE</span> : `-${c.discount_pct}%`}</td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs" data-testid={`code-scope-${c.code}`}>{appliesTo(c)}</td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs">{c.valid_from?.slice(0, 10)} → {c.valid_until?.slice(0, 10)}</td>
                  <td className="px-4 py-2.5 text-gray-500">{c.max_uses_per_person}</td>
                  <td className="px-4 py-2.5" data-testid={`code-used-${c.code}`}>{c.total_redeemed_months}{c.max_total_uses ? ` / ${c.max_total_uses}` : ""}</td>
                  <td className="px-4 py-2.5 text-gray-500">{c.unique_users}</td>
                  <td className="px-4 py-2.5">{c.active ? <span className="text-xs font-semibold bg-emerald-50 text-emerald-700 rounded-full px-2 py-0.5">active</span> : <span className="text-xs font-semibold bg-gray-100 text-gray-500 rounded-full px-2 py-0.5">off</span>}</td>
                  <td className="px-4 py-2.5"><button data-testid={`code-toggle-${c.code}`} onClick={() => toggle(c)} className="text-xs text-blue-600 hover:underline">{c.active ? "Deactivate" : "Activate"}</button></td>
                </tr>
                {reds[c.id] && (
                  <tr data-testid={`code-redemptions-panel-${c.code}`}>
                    <td colSpan={10} className="bg-gray-50/70 px-6 py-3 border-t border-gray-100">
                      <div data-testid={`code-redemptions-totals-${c.code}`} className="text-xs font-semibold text-gray-600 mb-2">
                        {reds[c.id].totals.count} redemption(s) · Collected <span className="text-emerald-700">${reds[c.id].totals.collected.toFixed(2)}</span> · Given away <span className="text-amber-700">${reds[c.id].totals.given_away.toFixed(2)}</span>
                      </div>
                      {reds[c.id].redemptions.length === 0 ? (
                        <p data-testid={`code-redemptions-empty-${c.code}`} className="text-xs text-gray-400">No redemptions yet</p>
                      ) : (
                        <table className="w-full text-xs">
                          <thead className="text-left text-[10px] uppercase text-gray-400">
                            <tr><th className="py-1 pr-3">Who</th><th className="py-1 pr-3">Type</th><th className="py-1 pr-3">When</th><th className="py-1 pr-3">What</th><th className="py-1 pr-3">Months</th><th className="py-1 pr-3">Full</th><th className="py-1 pr-3">Paid</th><th className="py-1">Given away</th></tr>
                          </thead>
                          <tbody>
                            {reds[c.id].redemptions.map((r, i) => (
                              <tr key={i} data-testid={`redemption-row-${c.code}-${i}`} className="border-t border-gray-100 text-gray-700">
                                <td className="py-1.5 pr-3">{r.email}</td>
                                <td className="py-1.5 pr-3">{r.user_type === "team" ? <span className="text-[10px] font-semibold bg-blue-50 text-blue-700 rounded px-1.5 py-px">👥 {r.team_name}</span> : <span className="text-[10px] font-semibold bg-gray-100 text-gray-600 rounded px-1.5 py-px">👤 personal</span>}</td>
                                <td className="py-1.5 pr-3 text-gray-500">{r.used_at?.slice(0, 16).replace("T", " ")}</td>
                                <td className="py-1.5 pr-3 text-gray-500">{r.context}</td>
                                <td className="py-1.5 pr-3">{r.months}</td>
                                <td className="py-1.5 pr-3">${(r.full_price ?? 0).toFixed(2)}</td>
                                <td className="py-1.5 pr-3 font-semibold">${(r.paid ?? 0).toFixed(2)}</td>
                                <td className="py-1.5 text-amber-700">${(r.saved ?? 0).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
              {codes.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-sm text-gray-400" data-testid="codes-empty">No codes yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </AdminShell>
  );
}
